import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, fromEvent, merge, map, startWith, distinctUntilChanged, pairwise, filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AppVisibilityService {
  private visibilitySubject = new BehaviorSubject<boolean>(
    document.visibilityState === 'visible'
  );

  /** Observable to subscribe to continuously */
  readonly isVisible$ = this.visibilitySubject.asObservable();

  /**
   * Emits when visibility transitions from false -> true (i.e. resume/foreground).
   * Useful to run a one-off sync.
   */
  readonly onResume$ = this.isVisible$.pipe(
    distinctUntilChanged(),
    pairwise(),                        // [previous, current]
    filter(([prev, curr]) => !prev && curr),
    map(() => void 0)                  // emit undefined (or map(() => true) if you prefer)
  );

  constructor(private ngZone: NgZone) {
    // run outside Angular to reduce CD cycles
    this.ngZone.runOutsideAngular(() => {
      const visibility$ = fromEvent(document, 'visibilitychange').pipe(
        map(() => document.visibilityState === 'visible') // <-- map used instead of deprecated mapTo
      );

      const focus$ = fromEvent(window, 'focus').pipe(map(() => true));
      const blur$ = fromEvent(window, 'blur').pipe(map(() => false));

      merge(visibility$, focus$, blur$)
        .pipe(
          startWith(document.visibilityState === 'visible'),
          distinctUntilChanged() // avoid repeated identical emissions
        )
        .subscribe((isVisible) => {
          // bring change back into Angular zone
          this.ngZone.run(() => this.visibilitySubject.next(isVisible));
        });
    });
  }
}
